import { diagnosticLevelRequirements } from '../dataModel/generated/diagnostic';
import { createDiagnosticGettersByGroup } from '../dataModel/generated/createDiagnosticGetters';
import { DiagnosticLevel } from '../dataModel/generated/DiagnosticLevel';
import { DiagnosticReponse } from '../dataModel/DiagnosticReponse';
import { LogementAccessor } from '../services/LogementAccessor';

/**
 * Compute the score (count of "Oui" responses) for a group of questions.
 * @param getters array of lambdas that return the answer for each question
 * @returns count of questions answered "Oui"
 */
const computeGroupScore = (getters: Array<() => DiagnosticReponse | undefined>): number => {
  return getters.filter(getter => getter() === DiagnosticReponse.Oui).length;
};

/**
 * Compute the diagnostic level (Accessible, Senior, 1*, 2*, 3*) based on answers.
 * Uses the requirements from calcul-niveaux.csv (stored in diagnosticLevelRequirements).
 *
 * Logic: For each level (from 3* down to Senior), check if ALL requirements for that level are met.
 * A requirement is met when: the group has at least MinYesCount answers == "Oui".
 */
export const computeHandiToitScore = (logementAccessor: LogementAccessor): DiagnosticLevel => {
  // Pre-compute getters mapped by group for type-safe evaluation
  const gettersByGroup = createDiagnosticGettersByGroup(logementAccessor);

  // Check levels from highest to lowest: 3* → 2* → 1* → Senior → Accessible
  const levelsToCheck = [
    DiagnosticLevel.ThreeEtoiles,
    DiagnosticLevel.TwoEtoiles,
    DiagnosticLevel.OneEtoile,
    DiagnosticLevel.Senior,
  ];

  for (const level of levelsToCheck) {
    const requirements = diagnosticLevelRequirements[level];
    if (!requirements) continue;

    // Check if ALL requirements for this level are met
    let allRequirementsMet = true;
    for (const [groupName, minYesCount] of Object.entries(requirements)) {
      const getters = gettersByGroup[groupName] || [];
      const groupScore = computeGroupScore(getters);

      if (groupScore < minYesCount) {
        allRequirementsMet = false;
        break;
      }
    }

    if (allRequirementsMet) {
      return level;
    }
  }

  return DiagnosticLevel.Accessible;
};
